import { useMemo, useState } from "react"
import type {
  EquipmentIssue,
  EquipmentComment,
  IssueStatus,
} from "../hooks/useEquipmentIssues"

type ActivityKind = "created" | "status" | "resolution" | "comment"

interface ActivityItem {
  key: string
  kind: ActivityKind
  issueId: string
  issueTitle: string
  unitLabel: string
  status: IssueStatus
  actorName: string
  at: string
  detail?: string
  relatedToMe: boolean
}

const KIND_META: Record<
  ActivityKind,
  { label: string; badge: string; dot: string; icon: string }
> = {
  created: {
    label: "신규 등록",
    badge: "bg-red-50 text-red-600 ring-1 ring-red-200",
    dot: "bg-red-500",
    icon: "M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z",
  },
  status: {
    label: "상태 변경",
    badge: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    dot: "bg-amber-500",
    icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  },
  resolution: {
    label: "조치결과",
    badge: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    dot: "bg-emerald-500",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  comment: {
    label: "코멘트",
    badge: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200",
    dot: "bg-indigo-500",
    icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  },
}

const STATUS_LABEL: Record<IssueStatus, string> = {
  issued: "이슈 등록",
  checking: "확인중",
  resolved: "조치완료",
  unavailable: "조치불가",
}

// 상대 시간 표기 (n분 전 / n시간 전 / n일 전)
function formatRelative(value?: string) {
  if (!value) return "-"
  const t = new Date(value).getTime()
  if (isNaN(t)) return "-"
  const diff = Date.now() - t
  if (diff < 60_000) return "방금 전"
  const min = Math.floor(diff / 60_000)
  if (min < 60) return `${min}분 전`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour}시간 전`
  const day = Math.floor(hour / 24)
  if (day < 31) return `${day}일 전`
  const d = new Date(t)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate()
  ).padStart(2, "0")}`
}

// 코멘트/조치결과 본문 요약 (앞 50자)
function summarize(text?: string, max = 50) {
  const s = (text || "").replace(/\s+/g, " ").trim()
  if (!s) return ""
  return s.length > max ? `${s.slice(0, max)}…` : s
}

// 사용자 식별값 정규화 (공백 제거 + 소문자)
function norm(value?: string) {
  return (value || "").trim().toLowerCase()
}

// 최근 활동 표시 기간 (최근 7일 이내만 노출)
const ACTIVITY_WINDOW_DAYS = 7
const ACTIVITY_WINDOW_MS = ACTIVITY_WINDOW_DAYS * 24 * 60 * 60 * 1000

interface Props {
  issues: EquipmentIssue[]
  comments: EquipmentComment[]
  myUserId?: string
  myEmail?: string
  myName?: string
  unreadByIssue: Record<string, number>
  unitLabelMap: Map<string, string>
  onSelectIssue: (issueId: string) => void
  limit?: number
}

export function EquipmentRecentActivity({
  issues,
  comments,
  myUserId,
  myEmail,
  myName,
  unreadByIssue,
  unitLabelMap,
  onSelectIssue,
  limit = 8,
}: Props) {
  const [open, setOpen] = useState(true)
  // '내 관련 활동(작성자 또는 참조자)만 보기' 필터
  const [onlyMine, setOnlyMine] = useState(false)
  // 필터 결과가 limit보다 많을 때 전체 펼치기
  const [showAll, setShowAll] = useState(false)

  // 로그인 사용자 식별 정보 (userId가 없는 레거시 데이터도 이메일/이름으로 매칭)
  const me = useMemo(
    () => ({
      id: (myUserId || "").trim(),
      email: norm(myEmail),
      name: norm(myName),
    }),
    [myUserId, myEmail, myName]
  )
  const hasIdentity = !!(me.id || me.email || me.name)

  // 이슈/코멘트를 하나의 타임라인으로 병합 → 최신순 정렬 (최근 7일 이내만)
  const activities = useMemo(() => {
    const cutoff = Date.now() - ACTIVITY_WINDOW_MS
    const issueMap = new Map<string, EquipmentIssue>()
    for (const i of issues) issueMap.set(i._id, i)

    // 사용자 한 명이 '나'인지 판정 (userId → 이메일 → 이름 순으로 비교)
    const isMe = (p: { userId?: string; email?: string; userName?: string }) => {
      if (!hasIdentity) return false
      if (me.id && p.userId && p.userId.trim() === me.id) return true
      if (me.email && norm(p.email) === me.email) return true
      if (me.name && norm(p.userName) === me.name) return true
      return false
    }

    // 내가 '참조자'로 지정된 이슈인지 판정 (작성자 여부는 제외)
    const isReferencedInIssue = (issue: EquipmentIssue) => {
      if (!hasIdentity) return false
      return (issue.referencedUsers || []).some((u) => isMe(u))
    }

    const list: ActivityItem[] = []

    for (const issue of issues) {
      const unitLabel = issue.unitId
        ? unitLabelMap.get(issue.unitId) || issue.unitLabel || "호기"
        : "공통항목"
      const base = {
        issueId: issue._id,
        issueTitle: issue.title?.trim() || issue.issueContent || "제목없음",
        unitLabel,
        status: issue.status,
        relatedToMe: isReferencedInIssue(issue),
      }

      // 신규 등록
      list.push({
        ...base,
        key: `created-${issue._id}`,
        kind: "created",
        actorName: issue.creatorName || "-",
        at: issue.createdAt,
      })

      // 조치결과 등록
      if (issue.resolvedAt && issue.resolutionContent?.trim()) {
        list.push({
          ...base,
          key: `resolution-${issue._id}`,
          kind: "resolution",
          actorName: issue.resolvedByName || "-",
          at: issue.resolvedAt,
          detail: summarize(issue.resolutionContent),
        })
      }

      // 상태/내용 변경 (생성 이후 갱신되었고, 조치결과 시각과 다른 경우)
      const created = new Date(issue.createdAt).getTime()
      const updated = new Date(issue.updatedAt).getTime()
      const resolved = issue.resolvedAt ? new Date(issue.resolvedAt).getTime() : 0
      if (
        !isNaN(created) &&
        !isNaN(updated) &&
        updated - created > 2000 &&
        Math.abs(updated - resolved) > 2000
      ) {
        list.push({
          ...base,
          key: `status-${issue._id}`,
          kind: "status",
          actorName: issue.creatorName || "-",
          at: issue.updatedAt,
          detail: `현재 상태: ${STATUS_LABEL[issue.status]}`,
        })
      }
    }

    // 코멘트
    for (const c of comments) {
      const issue = issueMap.get(c.issueId)
      if (!issue) continue
      const unitLabel = issue.unitId
        ? unitLabelMap.get(issue.unitId) || issue.unitLabel || "호기"
        : "공통항목"
      list.push({
        key: `comment-${c._id}`,
        kind: "comment",
        issueId: issue._id,
        issueTitle: issue.title?.trim() || issue.issueContent || "제목없음",
        unitLabel,
        status: issue.status,
        actorName: c.creatorName || "-",
        at: c.createdAt,
        detail: summarize(c.content),
        // 코멘트는 상위 이슈의 참조자이거나, 해당 코멘트에서 @멘션된 경우만 '내 관련'
        relatedToMe:
          isReferencedInIssue(issue) ||
          (c.mentionedUsers || []).some((u) => isMe(u)),
      })
    }

    // 최근 7일 이내 활동만 표시
    return list
      .filter((a) => {
        if (!a.at) return false
        const t = new Date(a.at).getTime()
        return !isNaN(t) && t >= cutoff
      })
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
  }, [issues, comments, me, hasIdentity, unitLabelMap])

  const mineCount = useMemo(
    () => activities.filter((a) => a.relatedToMe).length,
    [activities]
  )

  // 토글 적용 결과 (더 보기 이전의 전체 목록)
  const filtered = useMemo(
    () => (onlyMine ? activities.filter((a) => a.relatedToMe) : activities),
    [activities, onlyMine]
  )

  const visible = useMemo(
    () => (showAll ? filtered : filtered.slice(0, limit)),
    [filtered, showAll, limit]
  )

  const hiddenCount = filtered.length - visible.length

  // 토글 전환 시 펼침 상태 초기화
  const toggleOnlyMine = () => {
    setOnlyMine((v) => !v)
    setShowAll(false)
    setOpen(true)
  }

  if (activities.length === 0) return null

  return (
    <div className="mb-5 rounded-2xl border border-orange-200 bg-orange-50/40 overflow-hidden">
      {/* 헤더 */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white/70 border-b border-orange-100">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-orange-100 text-orange-600 shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </span>
          <h3 className="text-sm font-bold text-gray-900">
            최근 7일 이내 활동
            <span className="ml-1.5 font-normal text-gray-400">
              {onlyMine
                ? `내 관련 ${filtered.length}건 / 전체 ${activities.length}건`
                : `전체 ${activities.length}건`}
            </span>
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {/* 내 관련 활동만 보기 토글 */}
          <button
            type="button"
            role="switch"
            aria-checked={onlyMine}
            onClick={toggleOnlyMine}
            disabled={!hasIdentity}
            title="내가 참조자로 지정된 이슈, 또는 코멘트에서 @로 멘션된 활동만 표시"
            className={`inline-flex items-center gap-2 pl-2 pr-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              onlyMine
                ? "bg-orange-600 text-white border-orange-600 hover:bg-orange-700"
                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}
          >
            <span
              className={`relative inline-block w-8 h-4 rounded-full transition-colors ${
                onlyMine ? "bg-white/40" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${
                  onlyMine ? "left-4" : "left-0.5"
                }`}
              />
            </span>
            내 관련 활동만
            <span
              className={`px-1.5 rounded-full text-[11px] font-semibold ${
                onlyMine ? "bg-white/25 text-white" : "bg-gray-100 text-gray-500"
              }`}
            >
              {mineCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {open ? "접기" : "펼치기"}
            <svg
              className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {open ? (
        visible.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-gray-500">
              최근 7일 이내에 내가 참조자로 지정되거나 @로 멘션된 활동이 없습니다.
            </p>
            <button
              type="button"
              onClick={toggleOnlyMine}
              className="mt-1 text-xs text-orange-600 hover:text-orange-700 underline"
            >
              전체 활동 보기
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-orange-100">
            {visible.map((a) => {
              const meta = KIND_META[a.kind]
              const unread = unreadByIssue[a.issueId] || 0
              return (
                <li key={a.key}>
                  <button
                    type="button"
                    onClick={() => onSelectIssue(a.issueId)}
                    title="클릭하면 해당 이슈로 이동합니다"
                    className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-white/80 transition-colors"
                  >
                    {/* 안읽은 코멘트 표시용 좌측 점 */}
                    <span className="relative flex items-center justify-center w-6 h-6 shrink-0 mt-0.5">
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-white ${meta.dot}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={meta.icon} />
                        </svg>
                      </span>
                      {unread > 0 ? (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-orange-500 ring-2 ring-white" />
                      ) : null}
                    </span>

                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[11px] font-semibold rounded ${meta.badge}`}>
                          {meta.label}
                        </span>
                        <span className="text-sm font-medium text-gray-800 truncate max-w-full">
                          {a.issueTitle}
                        </span>
                        {a.relatedToMe && !onlyMine ? (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-orange-100 text-orange-700">
                            내 관련
                          </span>
                        ) : null}
                      </span>
                      {a.detail ? (
                        <span className="block text-xs text-gray-500 mt-0.5 truncate">{a.detail}</span>
                      ) : null}
                      <span className="block text-[11px] text-gray-400 mt-0.5">
                        {a.unitLabel} · {a.actorName} · {formatRelative(a.at)}
                        {unread > 0 ? (
                          <span className="ml-1.5 text-orange-600 font-semibold">
                            읽지 않은 코멘트 {unread}개
                          </span>
                        ) : null}
                      </span>
                    </span>

                    <svg
                      className="w-4 h-4 text-gray-300 shrink-0 mt-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </li>
              )
            })}
            {hiddenCount > 0 || showAll ? (
              <li className="px-4 py-2.5 text-center bg-white/50">
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="text-xs font-medium text-orange-600 hover:text-orange-700 underline"
                >
                  {showAll ? "간단하게 보기" : `더 보기 (${hiddenCount}건)`}
                </button>
              </li>
            ) : null}
          </ul>
        )
      ) : null}
    </div>
  )
}
